

import { useEffect, useState } from "react";
import { Input } from "../../components/input";
import { Button } from "../../components/button";
import { Label } from "../../components/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/select";

interface DropdownOption {
  id: string;
  name: string;
  status: string;
}

interface GroupMasterFormValues {
  group: string;
  subGroup: string;
  subSubGroup: string;
  name: string;
  code: string;
  uom: string;
  bom: string;
  status: string;
}

interface GroupMasterFormProps {
  onSubmit: (data: GroupMasterFormValues) => void;
  initialValues?: Partial<GroupMasterFormValues>;
  groupValues?: DropdownOption[];
  subGroupValues?: DropdownOption[];
  subSubGroupValues?: DropdownOption[];
  fetchSubGroups: (groupId: string) => Promise<DropdownOption[]>;
  fetchSubSubGroups: (subGroupId: string) => Promise<DropdownOption[]>;
  isEdit?: boolean;
}

export function GroupMasterForm({
  onSubmit,
  groupValues = [],
  subGroupValues = [],
  subSubGroupValues = [],
  fetchSubGroups,
  fetchSubSubGroups,
  initialValues = {},
  isEdit = false,
}: GroupMasterFormProps) {
  const [form, setForm] = useState<GroupMasterFormValues>({
    group: initialValues.group || "",
    subGroup: initialValues.subGroup || "",
    subSubGroup: initialValues.subSubGroup || "",
    name: initialValues.name || "",
    code: initialValues.code || "",
    uom: initialValues.uom || "",
    bom: initialValues.bom || "0",
    status: initialValues.status || "1",
  });

  const [availableSubGroups, setAvailableSubGroups] = useState<DropdownOption[]>([]);
  const [availableSubSubGroups, setAvailableSubSubGroups] = useState<DropdownOption[]>([]);

  useEffect(() => {
    if (initialValues.group) {
      fetchSubGroups(initialValues.group).then(setAvailableSubGroups);
    }
    if (initialValues.subGroup) {
      fetchSubSubGroups(initialValues.subGroup).then(setAvailableSubSubGroups);
    }
  }, [initialValues, fetchSubGroups, fetchSubSubGroups]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(form);
  };

  const handleGroupChange = async (value: string) => {
    const subGroups = await fetchSubGroups(value);
    setForm(prev => ({
      ...prev,
      group: value,
      subGroup: "",
      subSubGroup: "",
    }));
    setAvailableSubGroups(subGroups);
    setAvailableSubSubGroups([]);
  };

  const handleSubGroupChange = async (value: string) => {
    const subSubGroups = await fetchSubSubGroups(value);
    setForm(prev => ({
      ...prev,
      subGroup: value,
      subSubGroup: "",
    }));
    setAvailableSubSubGroups(subSubGroups);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 py-4">
      {/* <Input 
        name="name" 
        placeholder="Name" 
        value={form.name} 
        onChange={handleChange} 
        required 
      />

      <Input 
        name="code" 
        placeholder="Code" 
        value={form.code} 
        onChange={handleChange} 
      /> */}

      {/* Group */}
      <div>
        <Label>Group</Label>
        <Select 
          value={form.group} 
          onValueChange={handleGroupChange}
          required
        >
          <SelectTrigger>
            <SelectValue placeholder="Select Group" >
            {(groupValues || []).find(g => String(g.id) === String(form.group))?.name}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {groupValues.map((g) => (
              <SelectItem key={g.id} value={g.id}>
                {g.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Sub Group */}
      <div>
        <Label>Sub Group</Label>
        <Select 
          value={form.subGroup} 
          onValueChange={handleSubGroupChange}
          disabled={!form.group}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select Sub Group">
            {(availableSubGroups || []).find(sg => String(sg.id) === String(form.subGroup))?.name}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {availableSubGroups.map((sg) => (
              <SelectItem key={sg.id} value={sg.id}>
                {sg.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Sub Sub Group */}
      <div>
        <Label>Sub Group</Label>
        <Select 
          value={form.subSubGroup} 
          onValueChange={(val) => setForm({...form, subSubGroup: val})}
          disabled={!form.subGroup}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select Sub Sub Group">
            {(availableSubSubGroups || []).find(ssg => String(ssg.id) === String(form.subSubGroup))?.name}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {availableSubSubGroups.map((ssg) => (
              <SelectItem key={ssg.id} value={ssg.id}>
                {ssg.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* UOM */}
      <div>
        <Label>UOM</Label>
        <Input 
          name="uom" 
          placeholder="UOM" 
          value={form.uom} 
          onChange={handleChange} 
          required 
        />
      </div>

      {/* BOM */}
      <div>
        <Label>BOM</Label>
        <Select 
          value={form.bom} 
          onValueChange={(val) => setForm({...form, bom: val})}
        >
          <SelectTrigger>
            <SelectValue placeholder="BOM" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1">Yes</SelectItem>
            <SelectItem value="0">No</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Status */}
      <div>
        <Label>Status</Label>
        <Select 
          value={form.status} 
          onValueChange={(val) => setForm({...form, status: val})}
        >
          <SelectTrigger>
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1">Enabled</SelectItem>
            <SelectItem value="0">Disabled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Button type="submit">
        {isEdit ? "Update Group Master" : "Add Group Master"}
      </Button>
    </form>
  );
}